import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Decimal from 'decimal.js'
import NumberFormat from 'react-number-format'

import {
  getBalance,
  getPrimaryEmailAddress,
  getPrimaryPhoneNumber,
  getVerifiedEmailAddresses,
  getVerifiedPhoneNumbers,
} from 'models/ProfileInfo/selectors'

import { STRING, SYMBOLS } from 'resources'
import { APP_LINK } from 'resources/urls'

import getDecimalValue from 'utils/get-decimal-value'
import formatCurrency from 'utils/format-currency'
import formatPoints, { convertAmountToPoints, convertPointsToAmount } from 'utils/format-points'

import TextInput from 'components/TextInput'
import DropDownPicker from 'components/Picker'
import PrimaryLink from 'units/PrimaryLink'

import {
  Form,
  SectionContainer,
  SectionSeparator,
  SectionBoldText,
  SectionText,
  CancelButton,
  RedemptionProcessNote,
  RedemptionProcessNoteContainer,
  RedemptionSelectionButton,
  RedemptionProcessNoteWarning,
} from './styles'

const CashAmountSelector = ({
  reward,
  redemptionDestination,
  setRedemptionDestination,
  selectedAmount,
  setSelectedAmount,
  onRequest,
}) => {
  const balance = useSelector(getBalance)
  const primaryEmailAddress = useSelector(getPrimaryEmailAddress)
  const primaryPhoneNumber = useSelector(getPrimaryPhoneNumber)
  const verifiedEmailAddresses = useSelector(getVerifiedEmailAddresses)
  const verifiedPhoneNumbers = useSelector(getVerifiedPhoneNumbers)

  const emailDestinations = useMemo(
    () => verifiedEmailAddresses.map((entry) => entry.emailAddress),
    [verifiedEmailAddresses],
  )
  const phoneDestinations = useMemo(
    () => verifiedPhoneNumbers.map((entry) => entry.phoneNumber),
    [verifiedPhoneNumbers],
  )

  const handleRequestAmountChange = useCallback(
    ({ value }) => {
      if (!value || value >= 0) {
        const valueToNumber = convertPointsToAmount(parseInt(value, 10) || 0)
        setSelectedAmount(valueToNumber.toFixed(2))
      }
    },
    [setSelectedAmount],
  )

  const totalValue = useMemo(
    () => Decimal.max(0, getDecimalValue(selectedAmount, 0).minus(reward.fees || 0)),
    [selectedAmount, reward.fees],
  )

  const minRedemptionValue = reward.redemptionValues[0] || 0
  const maxRedemptionValue = reward.redemptionValues[reward.redemptionValues.length - 1] || 0
  const minPoints = convertAmountToPoints(minRedemptionValue)
  const maxUserPoints = convertAmountToPoints(balance)
  const maxValidPoints = convertAmountToPoints(maxRedemptionValue)

  const points = convertAmountToPoints(selectedAmount)
  const isGreaterThanBalance = useMemo(() => maxUserPoints.lt(points), [points, maxUserPoints])
  const isLessThanMinimumBalance = useMemo(() => minPoints.gt(points), [points, minPoints])
  const isGreaterThanMaxRedemptionValue = useMemo(() => maxValidPoints.lt(points), [points, maxValidPoints])
  const isInvalid =
    isGreaterThanBalance ||
    isLessThanMinimumBalance ||
    isGreaterThanMaxRedemptionValue ||
    (reward.id === 'paypal' && !primaryEmailAddress) ||
    (reward.id === 'venmo' && !primaryPhoneNumber)

  let errorMessage
  if (isLessThanMinimumBalance) {
    errorMessage = STRING.wallet.redemptions.cashRedemption.minPointsAmountErrorMessage(
      formatPoints(minRedemptionValue, false, true),
    )
  } else if (isGreaterThanBalance) {
    errorMessage = STRING.wallet.redemptions.cashRedemption.pointsGreaterThanBalance
  } else if (isGreaterThanMaxRedemptionValue) {
    errorMessage = STRING.wallet.redemptions.cashRedemption.pointsGreaterThanMaxRedemptionValue
  }

  return (
    <Form onSubmit={onRequest}>
      <TextInput
        tag={NumberFormat}
        invalid={isLessThanMinimumBalance || isGreaterThanBalance || isGreaterThanMaxRedemptionValue}
        thousandSeparator
        suffix={STRING.wallet.redemptions.cashRedemption.pointsSuffix}
        value={points.toString()}
        placeholder={STRING.wallet.redemptions.cashRedemption.pointsPlaceholder}
        onValueChange={handleRequestAmountChange}
        errorMessage={errorMessage}
        allowNegative={false}
      />
      <SectionContainer>
        <SectionBoldText>{STRING.wallet.redemptions.cashRedemption.dollarAmount}</SectionBoldText>
        <SectionText>{formatCurrency(selectedAmount)}</SectionText>
      </SectionContainer>
      <SectionContainer>
        <SectionBoldText>{STRING.wallet.redemptions.cashRedemption.redemptionFee}</SectionBoldText>
        <SectionText>{`${SYMBOLS.minus} ${formatCurrency(reward.fees)}`}</SectionText>
      </SectionContainer>
      <SectionSeparator />
      <SectionContainer>
        <SectionBoldText>{STRING.wallet.redemptions.cashRedemption.dollarToReceive}</SectionBoldText>
        <SectionText>{formatCurrency(totalValue)}</SectionText>
      </SectionContainer>
      {reward.id === 'paypal' && !!primaryEmailAddress && (
        <RedemptionProcessNote>
          {STRING.wallet.redemptions.cashRedemption.accountSelector.paypal.title}
          <DropDownPicker
            onUpdate={setRedemptionDestination}
            value={redemptionDestination}
            pickerItems={emailDestinations}
            placeholder={STRING.wallet.redemptions.cashRedemption.accountSelector.paypal.placeholder}
          />
          <RedemptionProcessNoteWarning>
            {STRING.wallet.redemptions.cashRedemption.recipientNote.paypal}
          </RedemptionProcessNoteWarning>
        </RedemptionProcessNote>
      )}
      {reward.id === 'venmo' && !!primaryPhoneNumber && (
        <RedemptionProcessNote>
          {STRING.wallet.redemptions.cashRedemption.accountSelector.venmo.title}
          <DropDownPicker
            onUpdate={setRedemptionDestination}
            value={redemptionDestination}
            pickerItems={phoneDestinations}
            placeholder={STRING.wallet.redemptions.cashRedemption.accountSelector.venmo.placeholder}
          />
        </RedemptionProcessNote>
      )}
      <RedemptionSelectionButton onPress={onRequest} disabled={isInvalid}>
        {STRING.wallet.redemptions.cashRedemption.continueButton}
      </RedemptionSelectionButton>
      <CancelButton to={APP_LINK} tag={PrimaryLink}>
        {STRING.wallet.redemptions.giftCardRedemption.redeemCancelButton}
      </CancelButton>
      <RedemptionProcessNoteContainer>
        <RedemptionProcessNote>{STRING.wallet.redemptions.giftCardRedemption.redeemProcessNote}</RedemptionProcessNote>
      </RedemptionProcessNoteContainer>
    </Form>
  )
}

CashAmountSelector.propTypes = {
  reward: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    categoryId: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string.isRequired,
    redemptionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentPrice: PropTypes.string,
    dayChange: PropTypes.string,
    fees: PropTypes.string,
  }).isRequired,
  redemptionDestination: PropTypes.string.isRequired,
  setRedemptionDestination: PropTypes.func.isRequired,
  selectedAmount: PropTypes.string.isRequired,
  setSelectedAmount: PropTypes.func.isRequired,
  onRequest: PropTypes.func.isRequired,
}

export default CashAmountSelector
